import React, { useContext, useEffect, useRef, useState } from 'react'
import debounce from 'lodash/debounce'

import Button from '@react/react-spectrum/Button'
import FieldLabel from '@react/react-spectrum/FieldLabel'
import Heading from '@react/react-spectrum/Heading'
import { Table, TBody, TD, TH, THead, TR } from '@react/react-spectrum/Table'
import Textfield from '@react/react-spectrum/Textfield'
import Select from '@react/react-spectrum/Select'
import Switch from '@react/react-spectrum/Switch'
import { error as errorToast } from '@react/react-spectrum/Toast'

import Copy from '@react/react-spectrum/Icon/Copy'
import LockClosed from '@react/react-spectrum/Icon/LockClosed'
import Delete from '@react/react-spectrum/Icon/Delete'
import Edit from '@react/react-spectrum/Icon/Edit'
import Checkmark from '@react/react-spectrum/Icon/Checkmark'
import Close from '@react/react-spectrum/Icon/Close'

import { SUPER_USERS } from 'config/constants'
import { useProject } from 'hooks'
import { RootContext } from 'root'
import A from 'services/analytics'
import { trasferProjectOwnership, updatePermission, updatePassword, updatePublicStatus } from 'services/project'
import { copyInputByRef } from 'utils/clipboard'

function AccessForm() {
  const websiteUrl = useRef()
  const [newOwner, setNewOwner] = useState('')
  const { project, type, isOwner, isEditor, isObserver } = useProject()
  const { user } = useContext(RootContext)
  const [transferingOwnership, setTransferingOwnership] = useState(false)
  const [disableButtons, setDisableButtons] = useState(false)

  const isSuperUser = SUPER_USERS.includes(user.id)

  const onOwnershipTransfer = (e) => {
    if (!newOwner || newOwner.trim() === '') {
      errorToast('Failed to transfer ownership. Please add Owner ID to proceed')
      return
    }
    setDisableButtons(true)
    A.triggerTransferProject()
    trasferProjectOwnership(project?.meta?.id, newOwner)
    e.preventDefault()
    e.stopPropagation()
  }

  const setPublic = (status) => {
    A.triggerUpdatePermissions()
    updatePublicStatus(project?.meta?.id, status)
  }

  const setPermission = (user, permission) => {
    A.triggerUpdatePermissions()
    updatePermission(project?.meta?.id, user, permission)
  }
  const hasPermissions =
    project && (project.owner === user.id || project.editors?.includes(user.id) || project.observers?.includes(user.id))

  return (
    <div className='AccessForm'>
      {project.meta.type === 'mobile' && (
        <section>
          <Heading size='4'>Anonymous access link</Heading>
          <p>Use this project ID to load your mobile app content in any DX Demo app</p>
          <Textfield readOnly={true} value={project?.meta?.id} ref={websiteUrl} />
          <Button variant='action' icon={<Copy />} onClick={() => copyInputByRef(websiteUrl)} />
        </section>
      )}

      {hasPermissions && (
        <section>
          <Heading size='4'>Ownership</Heading>
          {!isOwner && (isObserver || isEditor) && (
            <>
              <p>
                You have {isObserver && 'View'} {isEditor && 'View & Edit'} permissions. They can be revoked by project
                owner or renounced with the button below.
              </p>
              <Button
                variant='warning'
                disabled={disableButtons}
                onClick={(e) => {
                  e.preventDefault()
                  setPermission(user.id, null)
                  setDisableButtons(true)
                }}
              >
                Renounce View {isEditor && ' & Edit'} Permission
              </Button>
            </>
          )}

          {isOwner && (
            <>
              <p>
                You are the current project owner. Owner has full project permissions and cannot renounce them. You can
                transfer project ownership to another user with the button below. Take extra care when providing new
                owner ID, as this change is irreversible.
              </p>
              {!transferingOwnership && (
                <Button
                  variant='secondary'
                  onClick={(e) => {
                    setTransferingOwnership(true)
                    e.preventDefault()
                  }}
                  disabled={!isOwner}
                >
                  Transfer Ownership
                </Button>
              )}
              {transferingOwnership && (
                <div className='projectSettings__transferOwnership'>
                  <FieldLabel label='New owner ID'>
                    <Textfield value={newOwner} onChange={setNewOwner} />
                    &nbsp;&nbsp;&nbsp;&nbsp;
                    <Button variant='warning' disabled={disableButtons} onClick={onOwnershipTransfer}>
                      Transfer Ownership
                    </Button>
                  </FieldLabel>
                </div>
              )}
            </>
          )}
        </section>
      )}

      {(isOwner || isSuperUser) && (
        <section>
          <Heading size='4'>Access Permissions</Heading>
          <p>
            You can specify access details of this project. If you select that <b>everyone can view</b> your project,
            then each person with a link will be able to browse but not modify it. Such people will be allowed to add
            themselves as project viewers, so that this project gets listed on their home page.
          </p>
          <p>
            If you prefer to keep your project private, switch this option off and optionally add DSN ID's of selected
            collaborators in the table below. Once added, you can specify whether they should be able to edit the
            project or only view it.
          </p>
          <Switch label={'Everyone can view'} defaultChecked={project.isPublic} onChange={setPublic} />
          <PermissionTable project={project} onChange={setPermission} />
        </section>
      )}

      {['web-modular', 'customer-hub', 'web-social'].includes(type) && isOwner && (
        <section>
          <Heading size='4'>Password protection</Heading>
          <p>
            You can add a password to your project. If you do so, the <b>Viewers</b> will be asked to enter the password
            before entering the project.
          </p>
          <PasswordHandler hasPassword={project?.hasPassword} projectId={project?.meta?.id} />
        </section>
      )}

      {isSuperUser && !isOwner && !isEditor && (
        <Button variant='cta' onClick={() => setPermission(user.id, 'edit')}>
          By the power of DSN core team: make me an editor!
        </Button>
      )}
    </div>
  )
}

function PasswordHandler({ hasPassword: _hasPassword, projectId }) {
  const [hasPassword, setHasPassword] = useState(_hasPassword)
  const [isInputEnabled, setIsInputEnabled] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [password, setPassword] = useState('')

  const _updatePassword = (newPassword) => {
    setIsLoading(true)
    updatePassword(projectId, newPassword).then(() => {
      A.triggerUpdatePassword()
      setHasPassword(!!newPassword)
      setIsLoading(false)
    })
  }

  const toggleInput = () => {
    setIsInputEnabled((prevState) => !prevState)
    setPassword('')
  }

  const removePassword = () => {
    _updatePassword('')
  }

  const submitPassword = () => {
    _updatePassword(password)
    toggleInput()
  }

  const handleKeyDown = ({ key }) => {
    if (key === 'Enter') {
      submitPassword()
    }
    if (key === 'Escape') {
      toggleInput()
    }
  }

  const renderActionButtons = () => {
    if (isInputEnabled) {
      return (
        <div>
          <Textfield
            placeholder='Enter a new password'
            value={password}
            onChange={setPassword}
            disabled={isLoading}
            onKeyDown={handleKeyDown}
          />
          <Button
            disabled={isLoading}
            variant='action'
            quiet
            aria-label='Submit password'
            icon={<Checkmark />}
            onClick={submitPassword}
          />
          <Button
            disabled={isLoading}
            variant='action'
            quiet
            aria-label='Cancel'
            icon={<Close />}
            onClick={toggleInput}
          />
        </div>
      )
    }

    if (!hasPassword) {
      return (
        <div>
          <Button variant='primary' disabled={isLoading} onClick={toggleInput} icon={<LockClosed />}>
            Set password
          </Button>
        </div>
      )
    }

    return (
      <div>
        <Button icon={<Edit />} variant='primary' onClick={toggleInput} disabled={isLoading}>
          Edit password
        </Button>
        <Button icon={<Delete />} variant='warning' quiet onClick={removePassword} disabled={isLoading}>
          Remove password
        </Button>
      </div>
    )
  }

  return (
    <>
      <div>
        <p>
          <em>The password for this project is currently</em> <strong>{hasPassword ? 'set' : 'not set'}</strong>.
        </p>
      </div>
      {renderActionButtons()}
    </>
  )
}

function PermissionTable({ project, onChange }) {
  const [rows, setRows] = useState([])
  const [newUser, setNewUser] = useState()

  const canAddUser =
    newUser && ![project.owner, ...(project.observers || []), ...(project.editors || [])].includes(newUser)

  const onRevoke = (user) => {
    setRows((rs) => rs.filter((r) => r.user !== user))
    onChange(user)
  }

  const addUser = useRef(
    debounce((user) => {
      onChange(user, 'view')
      setNewUser(null)
      setRows((rows) => [...rows, { user, permission: 'view' }])
    }, 1000)
  )

  useEffect(() => {
    const permissions = [
      ...(project.editors || []).map((user) => ({ user, permission: 'edit' })),
      ...(project.observers || []).map((user) => ({ user, permission: 'view' })),
    ]
    setRows(permissions.filter(({ user }) => user !== project.owner))
  }, [])

  return (
    <Table className='PermissionTable'>
      <THead>
        <TH>User</TH>
        <TH>Permissions</TH>
        <TH></TH>
      </THead>
      <TBody>
        {rows.map((row, idx) => (
          <TR key={'row' + idx}>
            <TD width='150px'>
              <span>{row.user}</span>
            </TD>
            <TD width='150px'>
              <Select
                defaultValue={row.permission}
                options={[
                  { label: 'View', value: 'view' },
                  { label: 'View & Edit', value: 'edit' },
                ]}
                onChange={(value) => onChange(row.user, value)}
              />
            </TD>
            <TD>
              <Button variant='tool' icon={<Delete />} onClick={() => onRevoke(row.user)} />
            </TD>
          </TR>
        ))}
        <TR>
          <TD>
            <Textfield
              placeholder='Enter user ID'
              value={newUser || ''}
              onChange={setNewUser}
              invalid={newUser === project.owner}
            />
          </TD>
          <TD onClick={() => newUser && addUser.current(newUser)}>
            <Button variant='action' quiet label='+ Add' disabled={!canAddUser} />
          </TD>
          <TD></TD>
        </TR>
      </TBody>
    </Table>
  )
}

export default AccessForm
